import React, { useState, useEffect, useRef, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import axios from "axios";
import {
  FaPlus,
  FaRegCalendarAlt,
  FaMicrophone,
  FaTrash,
  FaList,
  FaUserFriends,
  FaBuilding,
  FaStop,
} from "react-icons/fa";
import { IoIosApps } from "react-icons/io";
import "./Tasks.css";
import toast from "react-hot-toast"; // Add this line

// SVG Icon for the delete button (matching EmployeeForm)
const DeleteIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    width="20px"
    height="20px"
  >
    <path d="M17 6H22V8H20V21C20 21.5523 19.5523 22 19 22H5C4.44772 22 4 21.5523 4 21V8H2V6H7V3C7 2.44772 7.44772 2 8 2H16C16.5523 2 17 2.44772 17 3V6ZM18 8H6V20H18V8ZM13.4142 13.9997L15.182 15.7675L13.7678 17.1817L12 15.4139L10.2322 17.1817L8.81802 15.7675L10.5858 13.9997L8.81802 12.232L10.2322 10.8178L12 12.5855L13.7678 10.8178L15.182 12.232L13.4142 13.9997ZM15 4H9V6H15V4Z"></path>
  </svg>
);
// --- Employee Selector Modal ---
const EmployeeSelectorModal = ({ employees, selectedIds, onSave, onClose }) => {
  const { t } = useTranslation();
  const [localSelectedIds, setLocalSelectedIds] = useState(selectedIds || []);

  const handleToggle = (employeeId) => {
    setLocalSelectedIds((prev) => {
      const safeArray = prev || [];
      if (safeArray.includes(employeeId)) {
        return safeArray.filter(id => id !== employeeId);
      }
      return [...safeArray, employeeId];
    });
  };

  return (
    <div className="employee-selector-overlay" onClick={onClose}>
      <div
        className="employee-selector-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="employee-selector-header">
          <h4>{t("Select Employees")}</h4>
        </div>
        <div className="employee-selector-body">
          <div className="employee-list">
            {employees.map((emp) => (
              <div key={emp.id} className="employee-item">
                <input
                  type="checkbox"
                  id={`emp-${emp.id}`}
                  checked={localSelectedIds && localSelectedIds.includes(emp.id.toString())}
                  onChange={() => handleToggle(emp.id.toString())}
                />
                <label htmlFor={`emp-${emp.id}`}>{emp.username}</label>
              </div>
            ))}
          </div>
        </div>
        <div className="employee-selector-actions">
          <button type="button" className="button-secondary" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="button-primary"
            onClick={() => onSave(localSelectedIds)}
          >
            {t("Confirm")}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Reusable Day Selector Modal ---
// Now accepts a 'selectionMode' prop ('single' or 'multiple')
const DaysOfWeekModal = ({
  selectedDays,
  onSave,
  onClose,
  selectionMode = "multiple",
}) => {
  const { t } = useTranslation();
  const [localSelectedDays, setLocalSelectedDays] = useState(selectedDays);

  const days = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  const handleChange = (day) => {
    if (selectionMode === "single") {
      setLocalSelectedDays([day]); // For single selection, just replace the value
    } else {
      // For multiple selection, toggle the value
      setLocalSelectedDays((prev) =>
        prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
      );
    }
  };

  return (
    <div className="days-selector-overlay" onClick={onClose}>
      <div
        className="days-selector-content"
        onClick={(e) => e.stopPropagation()}
      >
        <h4>
          {selectionMode === "single" ? t("Select a Day") : t("Select Days")}
        </h4>
        <div className="days-list">
          {days.map((day) => (
            <div key={day} className="day-item">
              <input
                // Use radio buttons for single selection, checkboxes for multiple
                type={selectionMode === "single" ? "radio" : "checkbox"}
                id={day}
                name="dayOfWeek" // Name is important for radio button grouping
                checked={localSelectedDays.includes(day)}
                onChange={() => handleChange(day)}
              />
              <label htmlFor={day}>{t(day)}</label>
            </div>
          ))}
        </div>
        <div className="days-selector-actions">
          <button type="button" className="button-secondary" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="button-primary"
            onClick={() => onSave(localSelectedDays)}
          >
            {t("Confirm")}
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper to convert date strings to timestamps for the API
const convertDateToTimestamp = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).getTime();
};

const formatDateForInput = (timestamp) => {
  if (!timestamp) return "";
  return new Date(timestamp).toISOString().split("T")[0];
};

// TaskForm.js

// --- NEW HELPER FUNCTION for calculating the next scheduled date ---
const calculateNextScheduledDate = (pattern, daysOfWeek) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Start from the beginning of today

  if (pattern === "daily") {
    return today; // For 'daily', the start date is today.
  }

  if ((pattern === "weekly" || pattern === "custom") && daysOfWeek.length > 0) {
    const dayIndexMap = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };
    const todayIndex = today.getDay();
    const selectedDayIndexes = daysOfWeek.map((day) => dayIndexMap[day]).sort();

    // Check if one of the selected days is today or later this week
    for (const dayIndex of selectedDayIndexes) {
      if (dayIndex >= todayIndex) {
        const nextDate = new Date(today);
        nextDate.setDate(today.getDate() + (dayIndex - todayIndex));
        return nextDate;
      }
    }

    // If all selected days have passed this week, find the earliest day next week
    const firstDayNextWeekIndex = selectedDayIndexes[0];
    const nextDate = new Date(today);
    nextDate.setDate(
      today.getDate() + (7 - todayIndex + firstDayNextWeekIndex)
    );
    return nextDate;
  }

  return today; // Fallback to today
};

// --- Mock Data for Dropdowns and Field lookup ---
// const mockDropdownData = {
//   fields: [
//     { id: "1", name: "updated name of field" },
//     { id: "2", name: "Field B" },
//   ],
//   taskTypes: [
//     { id: "1", name: "زراعة" },
//     { id: "2", name: "تسميد" },
//     { id: "3", name: "ري" },
//   ],
//   taskLists: [
//     { id: "1", name: "قائمة مهام أسبوعية" },
//     { id: "2", name: "قائمة مهام يومية" },
//   ],
//   employees: [
//     { id: "202", name: "فارمر تعديل 2" },
//     { id: "203", name: "h.worker" },
//   ],
// };

const TaskForm = () => {
  const { t, i18n } = useTranslation();
  const { fieldId, taskId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = !!taskId;
  const { userData, token } = useContext(UserContext);

  // --- State Management ---
  const [formData, setFormData] = useState({
    name: "",
    fieldId: fieldId || "",
    taskCategoryId: "",
    taskListId: "",
    employeeId: "", // Keep for backward compatibility
    assigneeIds: [], // New field for multiple employees
    isRecurring: false,
    dateType: "specific", // 'specific' or 'period' for non-recurring
    fromDate: "",
    toDate: "",
    startTime: "",
    estimatedHours: "",
    estimatedMinutes: "",
    recurrencePattern: "", // 'daily', 'weekly', 'custom' for recurring
    recurrenceDays: [],
    description: "",
    state: "notStarted",
  });
  // const [taskLists, setTaskLists] = useState([
  //   {
  //     id: 1,
  //     name: "مهام المزرعة",
  //     fromDate: "2025-08-12",
  //     toDate: "2025-08-20",
  //   },
  //   {
  //     id: 2,
  //     name: "planned task list",
  //     fromDate: "2025-06-19",
  //     toDate: "2025-10-31",
  //   },
  //   { id: 3, name: "شروق", fromDate: "2024-11-14", toDate: "2024-12-11" },
  // ]);
  // const [currentFieldName, setCurrentFieldName] = useState("");
  const [isDaysModalOpen, setDaysModalOpen] = useState(false);
  const [isEmployeeModalOpen, setEmployeeModalOpen] = useState(false);
  // const [fields, setFields] = useState([]);
  const [taskCategories, setTaskCategories] = useState([]);
  const [taskLists, setTaskLists] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [currentFieldName, setCurrentFieldName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // START: UPDATED STATE AND REFS FOR REAL RECORDING
  const [audioState, setAudioState] = useState({
    status: "idle",
    audioURL: null,
    duration: "0:00", // Add duration to the state
    error: null,
  });
  // NEW: State and ref for the live recording timer
  const [recordingTime, setRecordingTime] = useState(0);
  const timerIntervalRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordStartTsRef = useRef(null); // high-res start time

  // record duration handlers
  const handleMetadataLoad = (e) => {
    const audio = e.target;
    if (audio.duration !== Infinity) {
      const formattedDuration = formatTime(audio.duration);
      // Update the duration in the state
      setAudioState((prev) => ({ ...prev, duration: formattedDuration }));
    }
  };
  // format time
  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === Infinity) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // State for attachments
  const [images, setImages] = useState([]);
  const imageInputRef = useRef(null);

  // This single, combined useEffect handles all initial data loading
  useEffect(() => {
    const loadForm = async () => {
      const {
        persistedFormData,
        persistedImages,
        persistedAudioState,
        selectedTaskListId,
      } = location.state || {};

      if (!userData?.Farm?.id || !token) return;
      const farmId = userData.Farm.id;
      const tenantId = userData.Tenant.id;

      try {
        const [fieldRes, categoriesRes, listsRes, employeesRes] =
          await Promise.all([
            axios.get(`/api/field/${fieldId}`, {
              headers: { Authorization: token },
            }),
            axios.get(`/api/taskCategory`, {
              headers: { Authorization: token },
            }),
            axios.get(`/api/taskList/findByFarmId/${farmId}`, {
              headers: { Authorization: token },
            }),
            axios.get(`/api/emp/findByTenantId/${tenantId}`, {
              headers: { Authorization: token },
            }),
          ]);

        if (fieldRes.data.items && fieldRes.data.items.length > 0) {
          setCurrentFieldName(fieldRes.data.items[0].name);
        }
        setTaskCategories(categoriesRes.data.items || []);
        setTaskLists(listsRes.data.items || []);
        const employeeList = employeesRes.data.items.map(
          (item) => item.Employee
        );
        setEmployees(employeeList || []);

        let finalFormData = {};

        if (persistedFormData) {
          finalFormData = persistedFormData;
          console.log("final form data presisted ->", finalFormData);
          // --- START: MODIFIED IMAGE LOGIC ---
          if (persistedImages && Array.isArray(persistedImages)) {
            // The persistedImages array contains the original File objects.
            // We need to regenerate new, valid blob URLs for their previews.
            const regeneratedImages = persistedImages.map((image) => {
              // This handles images the user had selected but not yet uploaded.
              // We check if the 'file' property is a File object.
              if (image.file instanceof File) {
                return {
                  ...image,
                  preview: URL.createObjectURL(image.file), // Regenerate the URL!
                };
              }
              // This handles existing images that might have been loaded from the server
              // in edit mode, which would have a regular http URL.
              return image;
            });
            setImages(regeneratedImages);
          }
          // --- END: MODIFIED IMAGE LOGIC ---
          if (persistedAudioState) {
            setAudioState(persistedAudioState);
          }
        } else if (isEditing && taskId) {
          const taskResponse = await axios.get(`/api/task/${taskId}`, {
            headers: { Authorization: token },
          });
          // CORRECTED: Get the top-level response object
          const taskApiResponse = taskResponse.data.items?.[0];
          console.log("task api response", taskApiResponse);
          // CORRECTED: Check for the existence of the nested 'task' object
          if (taskApiResponse && taskApiResponse.task) {
            // Use the nested task object for all data access
            const taskData = taskApiResponse.task;

            // Handle estimated duration conversion from minutes to hours/minutes
            let estimatedHours = "";
            let estimatedMinutes = "";
            if (taskData.estimatedDuration && taskData.estimatedDuration > 0) {
              const hours = Math.floor(taskData.estimatedDuration / 60);
              const minutes = taskData.estimatedDuration % 60;
              estimatedHours = hours > 0 ? hours.toString() : "";
              estimatedMinutes = minutes > 0 ? minutes.toString() : "";
            }

            // Handle start time formatting from ISO string to HH:mm
            let startTimeFormatted = "";
            if (taskData.startTime) {
              try {
                const date = new Date(taskData.startTime);
                startTimeFormatted = date.toTimeString().slice(0, 5); // Format as HH:mm
              } catch (e) {
                console.error("Error parsing start time:", e);
              }
            }

            finalFormData = {
              name: taskData.name || "",
              fieldId: fieldId || "",
              description: taskData.textDesc || "",
              taskCategoryId: taskData.taskCategoryId || "",
              taskListId: taskData.taskListId || "",
              employeeId: "", // Keep empty for compatibility
              assigneeIds: taskApiResponse.assignments ?
                taskApiResponse.assignments.map(a => a.employeeId.toString()) : [],
              isRecurring: taskData.isRecurring || false,
              dateType:
                !taskData.isRecurring && taskData.toDate
                  ? "period"
                  : "specific",
              fromDate: formatDateForInput(taskData.fromDate),
              toDate: formatDateForInput(taskData.toDate),
              startTime: startTimeFormatted,
              estimatedHours: estimatedHours,
              estimatedMinutes: estimatedMinutes,
              recurrencePattern: taskData.recurrencePattern || "",
              recurrenceDays: taskData.recurrenceDays || [],
              state: taskData.state || "notStarted",
            };

            // CORRECTED: Access 'audioDesc' from the nested task object
            if (taskData.audioDesc) {
              setAudioState({
                status: "recorded",
                audioURL: taskData.audioDesc,
                duration: "0:00",
                error: null,
              });
            }
            console.log("task data", taskData);
            if (taskData.imagesUrls && taskData.imagesUrls.length > 0) {
              let imageUrlArray = []; // Start with a clean array.

              // This code will run, splitting the long string into an array of individual URLs.
              imageUrlArray = taskData.imagesUrls.split(",");

              // Now, this .map() will work correctly because it's running on the newly created array.
              const existingImages = imageUrlArray.map((url) => ({
                file: null,
                preview: url.trim(), // Using .trim() is good practice to remove any stray whitespace
              }));
              setImages(existingImages);
            }
            console.log("final form data in edit mode ->", finalFormData);
          }
        } else {
          finalFormData = {
            name: "",
            fieldId: fieldId || "",
            taskCategoryId: "",
            taskListId: "",
            employeeId: "",
            assigneeIds: [], // Ensure assigneeIds is initialized as an array
            isRecurring: false,
            dateType: "specific",
            fromDate: "",
            toDate: "",
            startTime: "",
            estimatedHours: "",
            estimatedMinutes: "",
            recurrencePattern: "",
            recurrenceDays: [],
            description: "",
          };
          console.log("final form data default mode ->", finalFormData);
        }

        if (selectedTaskListId) {
          finalFormData.taskListId = selectedTaskListId;
        }

        setFormData(finalFormData);
      } catch (error) {
        console.error("Failed to load task form data", error);
      } finally {
        if (location.state) {
          navigate(location.pathname, { state: {}, replace: true });
        }
      }
    };

    loadForm();
    // By adding this comment, you are telling your team and the linter:
    // "I am intentionally not including location/navigate as dependencies
    // because this effect should only run when the task/field identity
    // changes, not when a navigation action within the effect itself occurs."
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, taskId, fieldId, userData, token]);
  // console.log("form data", formData);

  // Helper to find the name of the selected list
  const selectedTaskList = taskLists.find(
    (l) => l.taskList.id === formData.taskListId
  );
  const selectedTaskListName =
    selectedTaskList?.taskList.name || t("Select...");

  // --- Data Loading Effect ---
  // useEffect(() => {
  //   // Find and set the current field's name
  //   const field = mockDropdownData.fields.find((f) => f.id === fieldId);
  //   if (field) {
  //     setCurrentFieldName(field.name);
  //   }

  //   if (isEditing) {
  //     // API Call: GET /api/task/{taskId}
  //     // Populate form with mock data for an existing task
  //     setFormData({
  //       name: "مهمو 2",
  //       fieldId: fieldId,
  //       taskCategoryId: "1",
  //       taskListId: "1",
  //       employeeId: "202",
  //       isRecurring: false,
  //       dateType: "specific",
  //       fromDate: "2025-02-23",
  //       toDate: "",
  //       recurrencePattern: "",
  //       recurrenceDays: [],
  //       description: "وصف المهمة",
  //     });
  //   }
  // }, [isEditing, taskId, fieldId]);

  // --- Handlers ---
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleRecurrencePatternChange = (pattern) => {
    // Clear previous day selections when changing the pattern for a clean state
    setFormData((prev) => ({
      ...prev,
      recurrencePattern: pattern,
      recurrenceDays: [],
    }));
    if (pattern === "weekly" || pattern === "custom") {
      setDaysModalOpen(true);
    }
  };

  const handleSaveDays = (days) => {
    setFormData((prev) => ({ ...prev, recurrenceDays: days }));
    setDaysModalOpen(false);
  };

  const handleSaveEmployees = (selectedIds) => {
    setFormData((prev) => ({ ...prev, assigneeIds: selectedIds }));
    setEmployeeModalOpen(false);
  };

  const handleRemoveEmployee = (employeeIdToRemove) => {
    setFormData((prev) => ({
      ...prev,
      assigneeIds: prev.assigneeIds.filter((id) => id !== employeeIdToRemove),
    }));
  };

  const selectedEmployees = employees.filter((emp) =>
    formData.assigneeIds && formData.assigneeIds.includes(emp.id.toString())
  );

  const handleImageFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files); // Convert FileList to Array
      const newImages = files.map((file) => ({
        file: file,
        preview: URL.createObjectURL(file),
      }));
      setImages((prev) => [...prev, ...newImages]);
    }
  };

  // In the removeImage function
  const removeImage = (index) => {
    const imageToRemove = images[index];
    if (imageToRemove && imageToRemove.preview.startsWith("blob:")) {
      URL.revokeObjectURL(imageToRemove.preview); // Revoke the URL
    }
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Add a cleanup effect for when the component unmounts
  useEffect(() => {
    // This is the cleanup function that runs when the component unmounts
    return () => {
      images.forEach((image) => {
        if (image.preview.startsWith("blob:")) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, [images]); // Run this effect whenever the images array changes

  // START: REAL AUDIO RECORDING HANDLERS
  const startRecording = async () => {
    setAudioState({ status: "permission", audioURL: null, error: null });
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const error = "Media Devices API not supported in this browser.";
      console.error(error);
      setAudioState({ status: "idle", audioURL: null, error });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      const getAccurateDuration = async (blob) => {
        try {
          // @ts-ignore - webkitAudioContext is a vendor prefix for older browsers
          const AudioCtx = window.AudioContext || window.webkitAudioContext;
          const audioCtx = new AudioCtx();
          const arrBuf = await blob.arrayBuffer();
          const audioBuf = await new Promise((resolve, reject) => {
            const ret = audioCtx.decodeAudioData(arrBuf, resolve, reject);
            if (ret && typeof ret.then === "function")
              ret.then(resolve).catch(reject);
          });
          audioCtx.close?.();
          return audioBuf.duration;
        } catch {
          return null;
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/wav",
        });
        const audioUrl = URL.createObjectURL(audioBlob);
        const approxSeconds =
          (performance.now() - recordStartTsRef.current) / 1000;

        setAudioState({
          status: "recorded",
          audioURL: audioUrl,
          duration: formatTime(approxSeconds),
          error: null,
        });
        // Now upgrade to precise duration
        const precise = await getAccurateDuration(audioBlob);
        if (precise && Math.abs(precise - approxSeconds) >= 1) {
          setAudioState((prev) =>
            prev.status === "recorded" && prev.audioURL === audioUrl
              ? { ...prev, duration: formatTime(precise) }
              : prev
          );
        }
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      recordStartTsRef.current = performance.now(); // mark start time
      setAudioState((prev) => ({ ...prev, status: "recording" }));
      setRecordingTime(0); // Reset timer
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      const error = "Microphone access was denied.";
      setAudioState({ status: "idle", audioURL: null, error });
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
      clearInterval(timerIntervalRef.current);
    }
  };

  const handleMicClick = () => {
    if (audioState.status === "idle") {
      startRecording();
    } else if (audioState.status === "recording") {
      stopRecording();
    }
  };

  const handleDeleteAudio = () => {
    if (audioState.audioURL) {
      URL.revokeObjectURL(audioState.audioURL);
    }
    setAudioState({ status: "idle", audioURL: null, error: null });
  };
  // END: REAL AUDIO RECORDING HANDLERS

  // const handleSubmit = (e) => {
  //   e.preventDefault();

  //   // 1. Create a FormData object to handle file uploads
  //   const submissionData = new FormData();

  //   // 2. Append the main form data as a single JSON string part named "task"
  //   // This matches the API documentation
  //   submissionData.append("task", JSON.stringify(formData));

  //   // 3. Append the recorded audio blob if it exists
  //   // The API expects this part to be named "audioFile"
  //   if (audioState.blob) {
  //     submissionData.append("audioFile", audioState.blob, "voice-note.wav");
  //   }

  //   // 4. Append all selected image files
  //   // The API expects this part to be named "imageFile"
  //   if (images.length > 0) {
  //     images.forEach((imageObject) => {
  //       submissionData.append("imageFile", imageObject.file);
  //     });
  //   }

  //   // --- API Call Simulation ---
  //   // In a real app, you would send `submissionData` as the body of your request.
  //   // The browser automatically sets the 'Content-Type' to 'multipart/form-data'.

  //   if (isEditing) {
  //     console.log("Submitting UPDATED data:", submissionData);
  //     // Example API Call:
  //     // fetch(`/api/task/updateTaskWithAudioAndImages`, {
  //     //   method: 'PUT',
  //     //   body: submissionData,
  //     // });
  //   } else {
  //     console.log("Submitting NEW data:", submissionData);
  //     // Example API Call:
  //     // fetch(`/api/task/addTaskWithAudioAndImages`, {
  //     //   method: 'POST',
  //     //   body: submissionData,
  //     // });
  //   }

  //   alert(isEditing ? "Task Updated!" : "Task Created!");
  //   navigate(`/fields/${fieldId}`);
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- 1. Frontend Validation ---
    if (
      !formData.name ||
      !formData.taskCategoryId ||
      !(formData.fromDate || formData.isRecurring) ||
      !formData.description
    ) {
      toast.error(
        t(
          "Please fill out all mandatory fields: Task Name, Task Type, Date, and Description."
        )
      );
      return;
    }

    setIsSubmitting(true);

    // --- 2. Get IDs from context and params ---
    const farmId = userData?.Farm?.id;
    const tenantId = userData?.Tenant?.id;

    if (!farmId || !tenantId) {
      toast.error(t("Session invalid. Please login again."));
      setIsSubmitting(false);
      return;
    }

    // --- 3. Build the 'task' JSON payload to match backend schema ---
    const taskPayload = {
      // Required fields
      id: isEditing ? parseInt(taskId) : 0, // 0 or omit if creating
      name: formData.name,
      textDesc: formData.description,
      taskCategoryId: parseInt(formData.taskCategoryId),
      farmId,
      tenantId,
      fieldId: fieldId ? parseInt(fieldId) : null,
      state: formData.state ? formData.state : "notStarted",
      isRecurring: formData.isRecurring,

      // Dates
      fromDate: 0, // will override below
      toDate: null, // Use null instead of 0 for better backend compatibility
      startTime: null, // will calculate below
      executeDate: null, // if not used yet, keep null

      // Assignments - use assigneeIds for multiple employees
      employeeId: null, // Always null to use the new assigneeIds system
      assigneeIds: formData.assigneeIds && formData.assigneeIds.length > 0
        ? formData.assigneeIds.map((id) => parseInt(id))
        : [],

      // Extra fields
      taskListId: formData.taskListId ? parseInt(formData.taskListId) : null,
      estimatedDuration: 0, // will calculate below
      audioDesc: null, // backend may populate this after upload
      imagesUrls: null, // backend will fill after upload
    };

    // Calculate estimated duration in minutes (matching Android)
    let totalEstimatedMinutes = 0;
    if (formData.estimatedHours && formData.estimatedHours !== "") {
      totalEstimatedMinutes = parseInt(formData.estimatedHours) * 60;
    }
    if (formData.estimatedMinutes && formData.estimatedMinutes !== "") {
      totalEstimatedMinutes += parseInt(formData.estimatedMinutes);
    }
    taskPayload.estimatedDuration = totalEstimatedMinutes;

    // --- 4. Handle complex date logic ---
    if (taskPayload.isRecurring) {
      // For recurring tasks, calculate the next scheduled date for fromDate
      const nextOccurrenceDate = calculateNextScheduledDate(
        formData.recurrencePattern,
        formData.recurrenceDays
      );
      taskPayload.fromDate = nextOccurrenceDate.getTime();
      taskPayload.recurrencePattern = formData.recurrencePattern;

      // Handle start time for recurring tasks - use the next occurrence date with the specified time
      const startDateTime = new Date(nextOccurrenceDate.getTime()); // Clone to avoid modifying original
      if (formData.startTime) {
        // Use the next occurrence date with the user-specified time
        const [hours, minutes] = formData.startTime.split(':');
        startDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        taskPayload.startTime = startDateTime.toISOString();
      } else {
        // Use the next occurrence date with default time (00:00)
        startDateTime.setHours(0, 0, 0, 0);
        taskPayload.startTime = startDateTime.toISOString();
      }

      if (formData.toDate) {
        taskPayload.toDate = convertDateToTimestamp(formData.toDate);
      }
      if (
        (formData.recurrencePattern === "weekly" ||
          formData.recurrencePattern === "custom") &&
        formData.recurrenceDays.length > 0
      ) {
        taskPayload.recurrenceDays = formData.recurrenceDays;
      }
    } else {
      // For non-recurring tasks
      taskPayload.fromDate = convertDateToTimestamp(formData.fromDate);

      // Handle start time for non-recurring tasks
      if (formData.startTime && formData.fromDate) {
        const startDateTime = new Date(
          `${formData.fromDate}T${formData.startTime}:00`
        );
        taskPayload.startTime = startDateTime.toISOString();
      } else if (formData.fromDate) {
        // If no specific time is set, use the date with default time
        const startDateTime = new Date(formData.fromDate);
        startDateTime.setHours(0, 0, 0, 0);
        taskPayload.startTime = startDateTime.toISOString();
      }

      if (formData.dateType === "period" && formData.toDate) {
        taskPayload.toDate = convertDateToTimestamp(formData.toDate);
      }
    }

    console.log("task payload", taskPayload);

    // --- 5. Prepare FormData for file uploads ---
    const submissionData = new FormData();
    submissionData.append("task", JSON.stringify(taskPayload));

    if (audioState.audioURL) {
      try {
        const response = await fetch(audioState.audioURL);
        const blob = await response.blob();
        submissionData.append("audioFile", blob, "voice-note.wav");
      } catch (err) {
        console.error("Could not convert audio URL to blob:", err);
      }
    }

    if (images.length > 0) {
      images.forEach((imageObject) => {
        if (imageObject.file) {
          submissionData.append("imageFile", imageObject.file);
        }
      });
    }

    // --- 6. Determine endpoint and make the API call ---
    const endpoint = isEditing
      ? "api/task/updateTaskWithAudioAndImages"
      : "api/task/addTaskWithAudioAndImages";
    const method = isEditing ? "put" : "post";

    try {
      await axios({
        method,
        url: endpoint,
        data: submissionData,
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: token,
        },
      });

      toast.success(isEditing ? t("Task Updated!") : t("Task Created!"));
      navigate(`/fields/${fieldId}`);
    } catch (error) {
      console.error("Failed to save task:", error);

      // Handle conflict errors specifically
      if (error.response && error.response.status === 409) {
        const conflictData = error.response.data;
        const conflictDetails = conflictData.conflictDetails || [];

        // Format the date and time information for the new task
        const taskDate = formData.fromDate ?
          new Date(formData.fromDate).toLocaleDateString(
            i18n.language === 'ar' ? 'ar-EG' : 'en-US',
            { year: 'numeric', month: 'short', day: 'numeric' }
          ) : "";
        const startTime = formData.startTime || "00:00";

        // Calculate end time based on estimated duration
        let endTime = "";
        if (formData.startTime && (formData.estimatedHours || formData.estimatedMinutes)) {
          const [hours, minutes] = formData.startTime.split(':');
          const startDate = new Date();
          startDate.setHours(parseInt(hours), parseInt(minutes), 0);

          const totalMinutes = (parseInt(formData.estimatedHours) || 0) * 60 + (parseInt(formData.estimatedMinutes) || 0);
          const endDate = new Date(startDate.getTime() + totalMinutes * 60000);
          endTime = endDate.toTimeString().slice(0, 5);
        } else {
          endTime = startTime; // Fallback if no duration
        }

        // Build conflict messages for each employee using backend data
        if (conflictDetails.length > 0) {
          const conflictMessages = conflictDetails.map(detail => {
            const employeeName = detail.employeeName || t("Employee");

            // Helper function to format time in 12-hour format with AM/PM
            const formatTime12Hour = (date) => {
              console.log('formatTime12Hour input:', {
                date: date,
                dateString: date.toString(),
                hours: date.getHours(),
                minutes: date.getMinutes(),
                utcHours: date.getUTCHours(),
                utcMinutes: date.getUTCMinutes(),
                locale: i18n.language === 'ar' ? 'ar-EG' : 'en-US'
              });
              const result = date.toLocaleTimeString(
                i18n.language === 'ar' ? 'ar-EG' : 'en-US',
                {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                }
              );
              console.log('formatTime12Hour output:', result);
              return result;
            };

            // Parse the conflict time from backend
            let conflictStartTime = "";
            let conflictEndTime = "";
            let conflictDate = "";

            if (detail.conflictTime) {
              // Backend sends LocalDateTime without timezone (e.g., "2025-09-25T19:30")
              // This is UTC time - need to convert to local time
              // Example: 19:30 UTC = 22:30 UTC+3 (10:30 PM in your timezone)

              console.log('Processing conflict time:', {
                raw: detail.conflictTime,
                detail: detail
              });

              let conflictDateTime;

              // Check if the time string contains explicit timezone information
              // Look for timezone indicators at the end of the string (after the time part)
              // Format examples: "2025-09-25T19:30Z", "2025-09-25T19:30+03:00", "2025-09-25T19:30-05:00"
              const hasTimezone = detail.conflictTime.includes('Z') ||
                                detail.conflictTime.includes('+') ||
                                (detail.conflictTime.lastIndexOf('-') > 10); // Check if '-' appears after the date part

              console.log('Timezone check:', {
                conflictTime: detail.conflictTime,
                hasTimezone: hasTimezone,
                includesZ: detail.conflictTime.includes('Z'),
                includesPlus: detail.conflictTime.includes('+'),
                lastMinusIndex: detail.conflictTime.lastIndexOf('-'),
                hasMinusTimezone: detail.conflictTime.lastIndexOf('-') > 10
              });

              if (hasTimezone) {
                console.log('Using normal Date parsing (has timezone)');
                // If it has timezone info, parse normally
                conflictDateTime = new Date(detail.conflictTime);
              } else {
                console.log('Using UTC parsing (no timezone)');
                // Backend sends UTC time as LocalDateTime without 'Z'
                // Format can be: "2025-09-25T19:30" or "2025-09-25T19:30:00"
                // IMPORTANT: The backend stores times in UTC but sends them without timezone indicator

                // Remove milliseconds if present
                const baseTime = detail.conflictTime.includes('.') ?
                  detail.conflictTime.split('.')[0] : detail.conflictTime;

                // Parse the components to avoid JavaScript's automatic timezone interpretation
                const [datePart, timePart] = baseTime.split('T');
                const [year, month, day] = datePart.split('-').map(Number);
                const timeComponents = timePart.split(':').map(Number);
                const [hours, minutes, seconds = 0] = timeComponents;

                // Create a UTC date directly using Date.UTC
                // This interprets the hours/minutes as UTC and converts to local timezone
                conflictDateTime = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));

                console.log('Converting UTC time to local:', {
                  original: detail.conflictTime,
                  baseTime: baseTime,
                  parsedComponents: { year, month, day, hours, minutes, seconds },
                  utcDate: conflictDateTime,
                  utcString: conflictDateTime.toUTCString(),
                  localString: conflictDateTime.toString(),
                  localHours: conflictDateTime.getHours(),
                  localMinutes: conflictDateTime.getMinutes()
                });
              }

              // Format date based on locale
              conflictDate = conflictDateTime.toLocaleDateString(
                i18n.language === 'ar' ? 'ar-EG' : 'en-US',
                { year: 'numeric', month: 'short', day: 'numeric' }
              );

              // Format time in 12-hour format with AM/PM
              conflictStartTime = formatTime12Hour(conflictDateTime);

              // Calculate end time based on duration from backend
              if (detail.conflictDuration && detail.conflictDuration > 0) {
                const endDateTime = new Date(conflictDateTime.getTime() + detail.conflictDuration * 60000);
                conflictEndTime = formatTime12Hour(endDateTime);
              } else {
                // If no duration, assume 1 hour default
                const endDateTime = new Date(conflictDateTime.getTime() + 60 * 60000);
                conflictEndTime = formatTime12Hour(endDateTime);
              }
            } else {
              // Fallback to attempted task time if backend doesn't provide conflict time
              conflictDate = taskDate;

              // Convert fallback times to 12-hour format
              if (startTime) {
                const [hours, minutes] = startTime.split(':');
                const tempDate = new Date();
                tempDate.setHours(parseInt(hours), parseInt(minutes));
                conflictStartTime = formatTime12Hour(tempDate);
              } else {
                conflictStartTime = startTime;
              }

              if (endTime) {
                const [hours, minutes] = endTime.split(':');
                const tempDate = new Date();
                tempDate.setHours(parseInt(hours), parseInt(minutes));
                conflictEndTime = formatTime12Hour(tempDate);
              } else {
                conflictEndTime = endTime;
              }
            }

            // Include task name if available
            const taskInfo = detail.conflictTaskName ?
              ` (${detail.conflictTaskName})` : "";

            return t("{{employee}} has a task on {{date}} from {{startTime}} to {{endTime}}", {
              employee: employeeName,
              date: conflictDate,
              startTime: conflictStartTime,
              endTime: conflictEndTime
            }) + taskInfo;
          });

          // Display all conflicts without header
          const fullMessage = conflictMessages.join("\n");
          toast.error(fullMessage, { duration: 10000 }); // Increased duration for better readability
        } else {
          // Fallback to simple message if no details
          const conflictingEmployeeIds = conflictData.conflictingAssigneeIds || [];
          const employeeNames = conflictingEmployeeIds
            .map(id => employees.find(emp => emp.id === id)?.username || t("Employee"))
            .join(", ");

          toast.error(
            t("Task conflict: {{employees}} have tasks at this time", {
              employees: employeeNames
            }),
            { duration: 6000 }
          );
        }
      } else {
        toast.error(t("Failed to save task."));
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <>
      {isDaysModalOpen && (
        <DaysOfWeekModal
          selectedDays={formData.recurrenceDays}
          onSave={handleSaveDays}
          onClose={() => setDaysModalOpen(false)}
          selectionMode={
            formData.recurrencePattern === "weekly" ? "single" : "multiple"
          }
        />
      )}
      {isEmployeeModalOpen && (
        <EmployeeSelectorModal
          employees={employees}
          selectedIds={formData.assigneeIds}
          onSave={handleSaveEmployees}
          onClose={() => setEmployeeModalOpen(false)}
        />
      )}
      <div className="task-form-container">
        <div className="form-card">
          <header className="view-header">
            <h1 className="view-header-title-group">
              {isEditing ? t("Edit Task") : t("New Task")}
            </h1>
          </header>
          <form onSubmit={handleSubmit} className="task-form">
            <div className="form-group">
              <label htmlFor="name">{t("Task Name")} *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>{t("Select Sector")}</label>
              <div className="input-with-icon-display">
                <IoIosApps className="input-icon" />
                <span>{currentFieldName || "Field Name"}</span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="taskCategoryId">{t("Task Type")} *</label>
              <div className="input-with-icon">
                <FaBuilding className="input-icon" />
                <select
                  id="taskCategoryId"
                  name="taskCategoryId"
                  value={formData.taskCategoryId}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>
                    {t("Select...")}
                  </option>
                  {taskCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {i18n.language === "ar" ? cat.nameAr : cat.nameEn}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="taskListId">{t("Add task to a task list")}</label>
              <button
                type="button"
                className="custom-select-trigger"
                onClick={() =>
                  navigate(`/fields/${fieldId}/tasklists`, {
                    state: {
                      returnTo: location.pathname,
                      persistedFormData: formData,
                      persistedImages: images,
                      persistedAudioState: audioState,
                    },
                  })
                }
              >
                <div className="input-with-icon">
                  <FaList className="input-icon" />
                  <span>{selectedTaskListName}</span>
                </div>
              </button>
            </div>

            <div className="form-group">
              <label htmlFor="assigneeIds">
                {t("Add employees for the task")}
              </label>
              <div className="input-with-icon">
                <FaUserFriends className="input-icon" />
                <input
                  type="text"
                  readOnly
                  style={{ cursor: "pointer" }}
                  placeholder={t("Click to select employees")}
                  value={selectedEmployees.length > 0 ?
                    (selectedEmployees.length === 1 ?
                      t("1 employee selected") :
                      t("{{count}} employees selected", { count: selectedEmployees.length })) :
                    t("Select Employees")}
                  onClick={() => setEmployeeModalOpen(true)}
                />
              </div>
              {selectedEmployees.length > 0 && (
                <div className="selectedWorkersList">
                  {selectedEmployees.map((employee) => (
                    <div
                      key={employee.id}
                      className="selectedWorkerItem"
                    >
                      <span className="selectedWorkerName">
                        {employee.username}
                      </span>
                      <button
                        type="button"
                        className="deleteWorkerBtn"
                        onClick={() => handleRemoveEmployee(employee.id.toString())}
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group-inline">
              <input
                type="checkbox"
                id="isRecurring"
                name="isRecurring"
                checked={formData.isRecurring}
                onChange={handleChange}
              />
              <label htmlFor="isRecurring">{t("Recurring Task")}</label>
            </div>

            {!formData.isRecurring ? (
              <div className="date-options-wrapper">
                <div className="date-type-toggle">
                  <button
                    type="button"
                    className={formData.dateType === "period" ? "active" : ""}
                    onClick={() =>
                      setFormData((p) => ({ ...p, dateType: "period" }))
                    }
                  >
                    {t("Period")}
                  </button>
                  <button
                    type="button"
                    className={formData.dateType === "specific" ? "active" : ""}
                    onClick={() =>
                      setFormData((p) => ({ ...p, dateType: "specific" }))
                    }
                  >
                    {t("Specific Date")}
                  </button>
                </div>
                {formData.dateType === "specific" ? (
                  <>
                    <div className="form-group">
                      <label htmlFor="fromDate">{t("Task Date")} *</label>
                      <div className="input-with-icon">
                        <FaRegCalendarAlt className="input-icon" />
                        <input
                          type="date"
                          id="fromDate"
                          name="fromDate"
                          value={formData.fromDate}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="startTime">{t("Start time")}</label>
                      <div className="input-with-icon">
                        <FaRegCalendarAlt className="input-icon" />
                        <input
                          type="time"
                          id="startTime"
                          name="startTime"
                          value={formData.startTime}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>{t("Estimated time")}</label>
                      <div className="form-row">
                        <div
                          className="form-group"
                          style={{ flex: 1, marginRight: "10px" }}
                        >
                          <input
                            type="number"
                            id="estimatedHours"
                            name="estimatedHours"
                            value={formData.estimatedHours}
                            onChange={handleChange}
                            min="0"
                            placeholder={t("hours")}
                          />
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                          <input
                            type="number"
                            id="estimatedMinutes"
                            name="estimatedMinutes"
                            value={formData.estimatedMinutes}
                            onChange={handleChange}
                            min="0"
                            max="59"
                            placeholder={t("minutes")}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="fromDate">{t("From")} *</label>
                        <div className="input-with-icon">
                          <FaRegCalendarAlt className="input-icon" />
                          <input
                            type="date"
                            id="fromDate"
                            name="fromDate"
                            value={formData.fromDate}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="toDate">{t("To")} *</label>
                        <div className="input-with-icon">
                          <FaRegCalendarAlt className="input-icon" />
                          <input
                            type="date"
                            id="toDate"
                            name="toDate"
                            value={formData.toDate}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="startTime">{t("Start time")}</label>
                      <div className="input-with-icon">
                        <FaRegCalendarAlt className="input-icon" />
                        <input
                          type="time"
                          id="startTime"
                          name="startTime"
                          value={formData.startTime}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>{t("Estimated time")}</label>
                      <div className="estimated-time-form-row">
                        <div
                          className="form-group"
                          style={{ flex: 1, marginRight: "10px" }}
                        >
                          <input
                            type="number"
                            id="estimatedHours"
                            name="estimatedHours"
                            value={formData.estimatedHours}
                            onChange={handleChange}
                            min="0"
                            placeholder={t("hours")}
                          />
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                          <input
                            type="number"
                            id="estimatedMinutes"
                            name="estimatedMinutes"
                            value={formData.estimatedMinutes}
                            onChange={handleChange}
                            min="0"
                            max="59"
                            placeholder={t("minutes")}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="form-group recurrence-options">
                  <label>{t("Select Period")}</label>
                  <div className="radio-group">
                    <label>
                      <input
                        type="radio"
                        name="recurrencePattern"
                        value="daily"
                        checked={formData.recurrencePattern === "daily"}
                        onChange={(e) =>
                          handleRecurrencePatternChange(e.target.value)
                        }
                      />
                      {t("Daily")}
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="recurrencePattern"
                        value="weekly"
                        checked={formData.recurrencePattern === "weekly"}
                        onChange={(e) =>
                          handleRecurrencePatternChange(e.target.value)
                        }
                      />
                      {t("Weekly")}
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="recurrencePattern"
                        value="custom"
                        checked={formData.recurrencePattern === "custom"}
                        onChange={(e) =>
                          handleRecurrencePatternChange(e.target.value)
                        }
                      />
                      {t("Custom")}
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="startTime">{t("Start time")}</label>
                  <div className="input-with-icon">
                    <FaRegCalendarAlt className="input-icon" />
                    <input
                      type="time"
                      id="startTime"
                      name="startTime"
                      value={formData.startTime}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t("Estimated time")}</label>
                  <div className="form-row">
                    <div
                      className="form-group"
                      style={{ flex: 1, marginRight: "10px" }}
                    >
                      <input
                        type="number"
                        id="estimatedHours"
                        name="estimatedHours"
                        value={formData.estimatedHours}
                        onChange={handleChange}
                        min="0"
                        placeholder={t("hours")}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <input
                        type="number"
                        id="estimatedMinutes"
                        name="estimatedMinutes"
                        value={formData.estimatedMinutes}
                        onChange={handleChange}
                        min="0"
                        max="59"
                        placeholder={t("minutes")}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="form-group">
              <label htmlFor="description">{t("Description")} *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows="4"
              ></textarea>
            </div>

            {/* UPDATED: Voice recorder and player section */}
            {audioState.status === "recorded" ? (
              <div className="audio-player-wrapper">
                <audio
                  key={audioState.audioURL}
                  onLoadedMetadata={handleMetadataLoad}
                  src={audioState.audioURL}
                  controls
                  className="audio-player"
                ></audio>
                <span className="audio-duration-display">{`${audioState.duration}`}</span>
                <button
                  type="button"
                  className="delete-audio-btn"
                  onClick={handleDeleteAudio}
                >
                  <FaTrash />
                </button>
              </div>
            ) : (
              <div className="audio-recorder-section">
                <button
                  type="button"
                  className={`mic-button ${
                    audioState.status === "recording" ? "recording" : ""
                  }`}
                  onClick={handleMicClick}
                  disabled={audioState.status === "permission"}
                >
                  {audioState.status === "recording" ? (
                    <FaStop />
                  ) : (
                    <FaMicrophone />
                  )}
                </button>
                {audioState.status === "recording" ? (
                  <span className="recording-timer">
                    {formatTime(recordingTime)}
                  </span>
                ) : (
                  <span>{t("Add task description with voice recording")}</span>
                )}
              </div>
            )}
            {audioState.error && (
              <p className="error-message">{t(audioState.error)}</p>
            )}

            <div className="form-row attachments-section">
              {/* UPDATED: Add the 'multiple' attribute to the file input */}
              <input
                type="file"
                accept="image/*"
                ref={imageInputRef}
                onChange={handleImageFileChange}
                style={{ display: "none" }}
                multiple
              />
              <div
                className="image-upload"
                onClick={() => imageInputRef.current.click()}
              >
                <FaPlus />
                <span>{t("Add Images")}</span>
              </div>
            </div>

            <div className="image-previews">
              {images.map((image, index) => (
                <div key={index} className="image-preview-item">
                  <img src={image.preview} alt="preview" />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={() => removeImage(index)}
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>

            <div className="form-actions">
              {/* NEW: Add the Cancel button */}
              <button
                type="button"
                className="button-secondary"
                onClick={() => {
                  // If we are editing, navigate to the task details page.
                  // Otherwise, navigate back to the list of tasks for the field.
                  if (isEditing) {
                    navigate(`/fields/${fieldId}/tasks/${taskId}`);
                  } else {
                    navigate(`/fields/${fieldId}`);
                  }
                }}
              >
                {t("Cancel")}
              </button>
              {/* UPDATED: Removed 'button-full-width' to allow buttons to sit side-by-side */}
              <button
                type="submit"
                className="button-primary"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? t("Saving...")
                  : isEditing
                  ? t("Save Changes")
                  : t("Save")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default TaskForm;
